/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (6 URL testées, révisé après re-vérification). Daté au moment des faits : 2026-06-13.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "printemps-ia-2026-besancon-cci-industrie",
  "title": "Printemps de l'IA 2026 : à Besançon, le PMT veut faire sortir l'IA des démonstrateurs",
  "excerpt": "La 3e édition du Printemps de l'IA s'est tenue le 11 juin à la CCI Saône-Doubs. Robotique, souveraineté, ateliers démonstratifs : le rendez-vous du PMT et de FEMTO-ST met en avant l'intégration.",
  "seoTitle": "Printemps de l'IA 2026 : l'IA dans l'usine",
  "seoDescription": "Troisième édition du Printemps de l'IA le 11 juin à la CCI Saône-Doubs : le Pôle des Microtechniques et FEMTO-ST misent sur l'intégration industrielle.",
  "category": "ia",
  "tags": [
    "Printemps de l'IA",
    "Pôle des Microtechniques",
    "FEMTO-ST",
    "CCI Saône-Doubs",
    "French Tech"
  ],
  "author": "jimmy",
  "publishedAt": "2026-06-13",
  "updatedAt": "2026-06-13",
  "featured": false,
  "cover": {
    "image": "/img/blog/besancon-la-cci-et-le-lycee-jules-haag.jpg",
    "credit": "Photo : Besançon, la CCI et le lycée Jules Haag — Wikipedro, CC BY-SA 4.0, via Wikimedia Commons",
    "alt": "Vue plongeante sur le bâtiment incurvé et vitré de la Chambre de commerce et d'industrie Saône-Doubs, avenue Villarceau à Besançon, avec le lycée Jules-Haag et les toits de la ville à l'arrière-plan"
  },
  "content": [
    {
      "type": "lead",
      "text": "La troisième édition du **Printemps de l'IA** s'est tenue jeudi 11 juin 2026 à la CCI Saône-Doubs, à Besançon, sur une journée complète[^3]. Organisée par le Pôle des Microtechniques (PMT) et [l'institut FEMTO-ST](/blog/femto-st-besancon-capitale-microtechniques), la journée était annoncée autour d'une bascule : une intelligence artificielle qui, selon le communiqué des organisateurs, « quitte les démonstrateurs pour s'intégrer au cœur des systèmes industriels », avec des enjeux de souveraineté et d'appropriation opérationnelle[^1]. Née en 2024, la manifestation s'est depuis installée à un rythme annuel[^4]."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "3e édition du Printemps de l'IA, journée dédiée à l'IA dans l'industrie"
        },
        {
          "label": "Quand",
          "value": "Jeudi 11 juin 2026, journée complète"
        },
        {
          "label": "Où",
          "value": "CCI Saône-Doubs, 46 avenue Villarceau, Besançon"
        },
        {
          "label": "Qui",
          "value": "PMT-Pôle des Microtechniques et institut FEMTO-ST"
        },
        {
          "label": "Pour qui",
          "value": "Professionnels uniquement : industriels, start-up, chercheurs (175 € HT, 100 € pour les adhérents PMT)"
        }
      ]
    },
    {
      "type": "h2",
      "text": "D'un travail d'acculturation à une question d'intégration"
    },
    {
      "type": "p",
      "text": "Le Printemps de l'IA est né en 2024 sous la forme d'une journée thématique. La première édition s'était tenue le 20 juin 2024 à la Maison de l'économie de Besançon, déjà à l'initiative du PMT et de FEMTO-ST, dans un format construit autour de conférences, de tables rondes et de cas d'usage à destination des PME industrielles[^5]. Deux ans plus tard, le contexte n'a plus grand-chose à voir. « Nos adhérents en avaient initié la création alors qu'ils entendaient parler plus ou moins précisément de l'IA et, pour certains, commençaient à découvrir par le concret la notion d'IA générative », rappelait avant l'événement Renaud Gaudillière, directeur du PMT[^2]."
    },
    {
      "type": "p",
      "text": "La deuxième édition, le 10 avril 2025, avait franchi un cran : plus de 200 professionnels — responsables innovation, directeurs techniques, ingénieurs production, maintenance ou qualité — y avaient abordé le traitement d'images, l'IA générative, les agents autonomes, la cybersécurité et le cadre normatif[^6]. Les décomptes varient toutefois d'une communication à l'autre : le bilan publié par le PMT en mai 2025 fait état de 18 offreurs de solutions au village[^6], quand sa communication de 2026 retient pour ce même millésime 200 participants, 21 exposants et 21 intervenants[^1]. Le pôle de compétitivité a par ailleurs été retenu comme « Ambassadeur IA » en Bourgogne-Franche-Comté au titre de l'appel à manifestation d'intérêt correspondant de l'État[^2]."
    },
    {
      "type": "h2",
      "text": "Robotique, souveraineté, implémentation : les trois axes de 2026"
    },
    {
      "type": "p",
      "text": "Si les échanges de 2025 avaient beaucoup porté sur l'automatisation des tâches, l'édition 2026 déplaçait le curseur. Le programme construisait les conférences et les tables rondes autour de l'IA et de la robotique — « une relation qui restait encore très hypothétique il y a un an à peine », relevait Renaud Gaudillière —, du sujet délicat de la souveraineté numérique, et de l'implémentation concrète de l'IA dans l'industrie[^2]. Le programme diffusé en amont annonçait également des séquences consacrées à l'IA et à l'humanité, ainsi qu'à la défense et à la souveraineté[^3]."
    },
    {
      "type": "p",
      "text": "Côté intervenants, le PMT avait mis en avant Pierre-Xavier Yeme (**Thales CIS**) et Arnaud Touré, ainsi que Pascal Rabier (**Google Cloud**), aux côtés de retours d'expérience et de cas d'usage censés illustrer la manière dont l'IA s'implémente réellement en production[^1]. La journée figurait à l'agenda de la Mission French Tech, qui rappelle que le PMT avait monté l'événement dès 2024 avec plusieurs laboratoires régionaux : FEMTO-ST, mais aussi les laboratoires [CIAD et ImViA](https://lafrenchtech.gouv.fr/fr/evenements/printemps-de-lia/)[^4]."
    },
    {
      "type": "h2",
      "text": "Des ateliers démonstratifs, principale nouveauté du format"
    },
    {
      "type": "p",
      "text": "La principale évolution annoncée pour 2026 tenait au format. Aux rendez-vous collectifs, au village d'exposants et aux cas d'usage, le programme ajoutait des ateliers démonstratifs : des sessions de 45 minutes à une heure, en petits groupes d'une quinzaine de personnes, réservées aux participants inscrits à la journée et animées par les entreprises partenaires[^2]. Six ateliers démonstratifs étaient annoncés, animés par sept partenaires[^2] :"
    },
    {
      "type": "ul",
      "items": [
        "**Orange**[^2] ;",
        "**Google**[^2] ;",
        "**Spie**[^2] ;",
        "**Atol CD**, société installée à Gevrey-Chambertin, en Côte-d'Or[^2] ;",
        "**Whyn**[^2] ;",
        "**Aprex**[^2] ;",
        "**[Dicte.ai](/blog/french-tech-night-2025-besancon-bfc20)**, start-up bisontine spécialisée dans l'assistance aux réunions[^2]."
      ]
    },
    {
      "type": "p",
      "text": "En parallèle, le village devait réunir 21 exposants[^2], parmi lesquels le PMT citait Atol CD, Dicte.ai, Insquest, LUCKiwi, Noé APP, Shiroo, Solanys, Whyn, Orange et SPIE[^1]. Le PMT présentait ce village comme un moyen de « répondre aux enjeux des entreprises industrielles souhaitant intégrer l'IA dans leurs processus »[^1]."
    },
    {
      "type": "callout",
      "tone": "info",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Le tissu microtechnique du bassin bisontin est fait de PME et d'ETI dont les process — usinage, contrôle qualité, maintenance — sont précisément là où l'IA promet des gains mesurables. En passant de la conférence de sensibilisation à l'atelier en groupe de quinze, le Printemps de l'IA s'adresse moins à la curiosité qu'à la décision d'investissement. Et en plaçant la souveraineté numérique au même niveau que la performance, il pose une question rarement traitée localement : sur quelle infrastructure ces outils tourneront-ils ?"
    },
    {
      "type": "h2",
      "text": "Un rendez-vous installé, des résultats à mesurer"
    },
    {
      "type": "p",
      "text": "Comme les années précédentes, environ 200 participants étaient attendus dans les locaux de [la CCI Saône-Doubs](/blog/open-business-cci-jerome-colombain-ia-entreprises-2024)[^2]. Le PMT n'avait pas encore publié, à la date de cet article, le bilan chiffré de la journée ni la liste définitive des cas industriels présentés ; nous y reviendrons. Reste l'intention, résumée par Renaud Gaudillière, qui voit dans l'événement l'expression même de ce qu'est un pôle de compétitivité : un point d'échanges entre PME, grands groupes et monde de la recherche-développement, où les rencontres fortuites des couloirs comptent aussi. « L'intelligence collective, c'est une force qui reste première », concluait-il[^2]. Pour les entreprises du bassin, la question ouverte est désormais moins celle de l'outil que celle de son intégration durable dans des systèmes industriels existants."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "La 3e édition du Printemps de l'IA fait son retour à la CCI de Besançon",
      "publisher": "macommune.info",
      "url": "https://www.macommune.info/la-3e-edition-du-printemps-de-lia-fait-son-retour-a-la-cci-de-besancon/",
      "type": "press",
      "date": "2026-06-04"
    },
    {
      "id": 2,
      "title": "A Besançon, le 3ème Printemps de l'IA est prêt à faire bourgeonner les idées",
      "publisher": "Traces Écrites News",
      "url": "https://www.tracesecritesnews.fr/actualite/a-besancon-le-3eme-printemps-de-l-ia-est-pret-a-faire-bourgeonner-les-idees-234194",
      "type": "press",
      "date": "2026-06-08"
    },
    {
      "id": 3,
      "title": "Printemps de l'IA 2026",
      "publisher": "Temis Technopole",
      "url": "https://www.temis.org/agenda/printemps-de-lia-2026/",
      "type": "official",
      "date": ""
    },
    {
      "id": 4,
      "title": "Printemps de l'IA",
      "publisher": "Mission French Tech",
      "url": "https://lafrenchtech.gouv.fr/fr/evenements/printemps-de-lia/",
      "type": "official",
      "date": ""
    },
    {
      "id": 5,
      "title": "1ère édition le PrinTemps de l'IA – Besançon",
      "publisher": "En direct, journal de l'Université de Franche-Comté",
      "url": "https://endirect.univ-fcomte.fr/evenements/1ere-edition-le-printemps-de-lia-besancon/",
      "type": "academic",
      "date": ""
    },
    {
      "id": 6,
      "title": "Bilan de la deuxième édition du Printemps de l'IA : l'intelligence artificielle s'ancre dans l'industrie",
      "publisher": "Pôle des Microtechniques",
      "url": "https://www.pmt-innovation.com/bilan-de-la-deuxieme-edition-du-printemps-de-lia-lintelligence-artificielle-sancre-dans-lindustrie/",
      "type": "official",
      "date": "2025-05-06"
    }
  ]
};
